
import { useState, useEffect, useRef } from 'react';

const BrandStory = () => {
  const [activePhase, setActivePhase] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const timelineRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastActivePhase = useRef(0);

  const storyPhases = [
    {
      year: '1716年',
      title: '传奇开篇',
      description: '马帮始祖创立茶号，开启三百年传承之路',
      content: '康熙五十五年，马帮先祖李瑞贡在普洱府创立茶号，专事贡茶制作。凭借精湛工艺和诚信经营，迅速成为朝廷指定贡茶供应商，奠定了品牌根基。',
      image: 'https://public.readdy.ai/ai/img_res/bb17f5d5b3abe037cfba6a829822bfdd.jpg'
    },
    {
      year: '1750-1850年',
      title: '技艺奠基',
      description: '马帮茶路兴盛，制茶工艺日臻完善',
      content: '茶马古道的黄金时期，马帮瑞贡茶叶沿着古道远销藏区、东南亚。在长途运输中，茶叶经历独特的后发酵过程，形成了独特的普洱茶制作工艺。',
      image: 'https://readdy.ai/api/search-image?query=Tea%20horse%20road%20caravan%20in%2018th%20century%2C%20horses%20carrying%20tea%20packages%20through%20mountain%20passes%2C%20misty%20ancient%20trade%20route%2C%20traditional%20Chinese%20merchants%2C%20historical%20trading%20scene%20with%20dramatic%20lighting&width=600&height=400&seq=story2&orientation=landscape'
    },
    {
      year: '1850-1950年',
      title: '历史沉浮',
      description: '百年风雨，传承不息，工艺秘方代代相传',
      content: '历经战乱和社会变迁，马帮瑞贡虽几度沉浮，但制茶技艺始终在家族中秘密传承。每一代传承人都在困境中坚守着对品质的执着。',
      image: 'https://readdy.ai/api/search-image?query=Old%20Chinese%20tea%20master%20teaching%20traditional%20tea%20making%20techniques%20to%20apprentice%2C%20vintage%20workshop%20setting%2C%20antique%20tea%20processing%20tools%2C%20historical%20preservation%20of%20craftsmanship%2C%20sepia%20tone%20atmosphere&width=600&height=400&seq=story3&orientation=landscape'
    },
    {
      year: '1980-2000年',
      title: '文化复兴',
      description: '改革开放春风，古法制茶重现辉煌',
      content: '改革开放后，第十二代传承人李文华重启马帮瑞贡品牌，将古法制茶工艺与现代科学相结合，开创了普洱茶现代化生产的先河。',
      image: 'https://readdy.ai/api/search-image?query=Modern%20tea%20factory%20in%201980s%20China%2C%20blend%20of%20traditional%20and%20modern%20tea%20processing%20equipment%2C%20workers%20in%20clean%20production%20environment%2C%20cultural%20revival%20atmosphere%2C%20industrial%20heritage&width=600&height=400&seq=story4&orientation=landscape'
    },
    {
      year: '2000-2020年',
      title: '当代焕新',
      description: '品牌重塑，荣获国际认可，走向世界舞台',
      content: '新千年后，马帮瑞贡全面品牌升级，获得有机认证、成为政府采购供应商，产品远销海外，在国际茶叶评比中屡获殊荣。',
      image: 'https://readdy.ai/api/search-image?query=Modern%20premium%20tea%20packaging%20and%20international%20tea%20exhibition%2C%20elegant%20product%20display%2C%20awards%20and%20certificates%2C%20global%20recognition%2C%20contemporary%20branding%20with%20traditional%20elements&width=600&height=400&seq=story5&orientation=landscape'
    },
    {
      year: '2020至今',
      title: '未来展望',
      description: '传承创新并举，打造世界级普洱茶品牌',
      content: '引入新加坡"Antique Pu \'er Tea Global Industry Investment PTE.LTD（古董普洱茶全球产业投资有限公司） "资本，完成现代化企业转型，注入国际视野与创新活力。\n未来展望： 秉承三百年精神，面向全球市场，持续讲述中国茶的故事。',
      image: 'https://readdy.ai/api/search-image?query=Future%20vision%20of%20tea%20industry%2C%20digital%20technology%20in%20tea%20cultivation%2C%20sustainable%20farming%20practices%2C%20modern%20tea%20culture%20center%2C%20innovative%20brand%20experience%2C%20bright%20optimistic%20atmosphere&width=600&height=400&seq=story6&orientation=landscape'
    }
  ];

  // 检测设备类型
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // 手机版滚动监听 - 修复闪烁问题
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const viewportCenter = window.innerHeight / 2;
      const viewportTop = window.scrollY;
      const viewportBottom = viewportTop + window.innerHeight;
      
      let bestIndex = lastActivePhase.current;
      let bestScore = -Infinity;

      timelineRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const elementTop = rect.top + viewportTop;
          const elementBottom = elementTop + rect.height;
          const elementCenter = elementTop + rect.height / 2;

          // 计算元素在视口中的可见度
          const visibleTop = Math.max(elementTop, viewportTop);
          const visibleBottom = Math.min(elementBottom, viewportBottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const visibilityRatio = visibleHeight / rect.height;

          // 计算距离屏幕中心的距离
          const distanceFromCenter = Math.abs(elementCenter - (viewportTop + viewportCenter));
          const maxDistance = window.innerHeight;
          const distanceScore = 1 - (distanceFromCenter / maxDistance);

          // 综合评分：可见度权重更高，距离中心权重较低
          const score = visibilityRatio * 0.7 + distanceScore * 0.3;

          // 为最后一个元素添加特殊处理，避免闪烁
          if (index === storyPhases.length - 1) {
            // 如果是最后一个元素且已经滚动到页面底部附近
            const documentHeight = document.documentElement.scrollHeight;
            const scrollProgress = (viewportBottom) / documentHeight;
            
            if (scrollProgress > 0.85 && visibilityRatio > 0.3) {
              // 给最后一个元素额外加分，确保稳定性
              const finalScore = score + 0.2;
              if (finalScore > bestScore) {
                bestScore = finalScore;
                bestIndex = index;
              }
            } else if (score > bestScore) {
              bestScore = score;
              bestIndex = index;
            }
          } else if (score > bestScore) {
            bestScore = score;
            bestIndex = index;
          }
        }
      });

      // 只有当新的索引与当前索引不同且评分差异足够大时才更新
      if (bestIndex !== lastActivePhase.current && bestScore > 0.1) {
        lastActivePhase.current = bestIndex;
        setActivePhase(bestIndex);
      }
    };

    // 添加防抖以减少频繁更新
    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 50);
    };

    window.addEventListener('scroll', debouncedScroll, { passive: true });
    // 初始检查
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      clearTimeout(timeoutId);
    };
  }, [isMobile, storyPhases.length]);

  return (
    <section id="story" className="py-12 lg:py-16 bg-white">
      <div className="px-6 lg:px-16" ref={containerRef}>
        <div className="text-center mb-8 lg:mb-12">
          <h2 className="text-2xl lg:text-3xl font-bold text-primary font-serif mb-3">
            品牌故事
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            三百年传承路，每一个历史节点都见证着马帮精神的延续与发展
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* 手机版布局 */}
          <div className="lg:hidden">
            <div className="space-y-8">
              {storyPhases.map((phase, index) => (
                <div key={index} className="relative">
                  {/* Timeline Item */}
                  <div
                    ref={(el) => (timelineRefs.current[index] = el)}
                    className={`relative pl-8 transition-all duration-700 ease-out ${
                      activePhase === index ? 'opacity-100 scale-105' : 'opacity-70'
                    }`}
                  >
                    {/* Timeline Line */}
                    <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200">
                      {index < storyPhases.length - 1 && (
                        <div
                          className={`w-full transition-all duration-1000 ease-out bg-accent ${
                            activePhase > index ? 'h-full' : 'h-0'
                          }`}
                        />
                      )}
                    </div>

                    {/* Timeline Dot */}
                    <div
                      className={`absolute left-1 top-2 w-5 h-5 rounded-full border-3 transition-all duration-700 ease-out ${
                        activePhase === index
                          ? 'bg-accent border-accent scale-125 shadow-lg'
                          : activePhase > index
                          ? 'bg-accent border-accent'
                          : 'bg-white border-gray-300'
                      }`}
                    />

                    <div className="ml-2 py-2">
                      <div className={`text-sm font-bold mb-1 transition-colors duration-500 ${
                        activePhase === index ? 'text-accent' : 'text-gray-500'
                      }`}>
                        {phase.year}
                      </div>
                      <h3 className={`text-xl font-bold font-serif mb-2 transition-colors duration-500 ${
                        activePhase === index ? 'text-primary' : 'text-gray-700'
                      }`}>
                        {phase.title}
                      </h3>
                      <p className={`text-gray-600 transition-colors duration-500 ${
                        activePhase === index ? 'text-gray-800' : 'text-gray-500'
                      }`}>
                        {phase.description}
                      </p>
                    </div>
                  </div>

                  {/* Mobile Detail Card - 优化展开动画，减少闪烁 */}
                  <div className={`ml-8 mt-4 overflow-hidden transition-all duration-700 ease-out ${
                    activePhase === index 
                      ? 'max-h-96 opacity-100 transform translate-y-0' 
                      : 'max-h-0 opacity-0 transform -translate-y-4'
                  }`}>
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 shadow-md border border-gray-100">
                      <img
                        src={phase.image}
                        alt={phase.title}
                        className="w-full h-48 object-cover object-top rounded-lg mb-4 shadow-sm"
                      />
                      <div className="text-sm text-accent font-bold mb-2">
                        {phase.year}
                      </div>
                      <p className="text-gray-700 leading-relaxed text-sm">
                        {phase.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* PC版布局保持不变 */}
          <div className="hidden lg:grid grid-cols-2 gap-12 items-center">
            {/* Timeline */}
            <div className="space-y-4">
              {storyPhases.map((phase, index) => (
                <div
                  key={index}
                  className={`relative pl-6 pb-6 cursor-pointer transition-all duration-300 ${
                    activePhase === index ? 'opacity-100' : 'opacity-60 hover:opacity-80'
                  }`}
                  onClick={() => setActivePhase(index)}
                >
                  {/* Timeline Line */}
                  <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gray-200">
                    {index < storyPhases.length - 1 && (
                      <div
                        className={`w-full transition-all duration-500 ${
                          activePhase >= index ? 'bg-accent' : 'bg-gray-200'
                        }`}
                        style={{ height: activePhase > index ? '100%' : '0%' }}
                      />
                    )}
                  </div>

                  {/* Timeline Dot */}
                  <div
                    className={`absolute left-0 top-0 w-4 h-4 rounded-full border-2 transition-all duration-300 ${
                      activePhase === index
                        ? 'bg-accent border-accent scale-125'
                        : activePhase > index
                        ? 'bg-accent border-accent'
                        : 'bg-white border-gray-300'
                    }`}
                  />

                  <div className="ml-4">
                    <div className="text-xs text-accent font-medium mb-1">{phase.year}</div>
                    <h3 className="text-lg font-bold text-primary font-serif mb-1">
                      {phase.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{phase.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Content Display */}
            <div className="sticky top-24">
              <div className="bg-gray-50 rounded-xl p-6">
                <img
                  src={storyPhases[activePhase].image}
                  alt={storyPhases[activePhase].title}
                  className="w-full h-64 object-cover object-top rounded-lg mb-4"
                />
                <div className="text-xs text-accent font-medium mb-2">
                  {storyPhases[activePhase].year}
                </div>
                <h3 className="text-xl font-bold text-primary font-serif mb-3">
                  {storyPhases[activePhase].title}
                </h3>
                <p className="text-gray-700 leading-relaxed text-sm">
                  {storyPhases[activePhase].content}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandStory;
